import { useState, useEffect, useCallback } from 'react';
import { Star, ExternalLink, ChevronLeft, ChevronRight, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Google "G" Logo SVG
const GoogleLogo = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays < 1) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

// Review Card Component
const ReviewCard = ({ review }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-rose-100/50 h-full flex flex-col">
    {/* Header with Google logo and author */}
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        {review.authorPhoto ? (
          <img
            src={review.authorPhoto}
            alt={review.authorName}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#E85D75] flex items-center justify-center text-white font-semibold">
            {review.authorName?.charAt(0) || 'A'}
          </div>
        )}
        <div>
          <p className="font-semibold text-stone-800 text-sm">
            {review.authorName}
          </p>
          <p className="text-xs text-stone-500">
            {review.relativeTime || formatRelativeTime(review.time)}
          </p>
        </div>
      </div>
      <GoogleLogo />
    </div>

    {/* Star Rating */}
    <div className="flex mb-3">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < review.rating
              ? 'text-amber-400 fill-amber-400'
              : 'text-stone-300'
          }`}
        />
      ))}
    </div>

    {/* Review Text */}
    <p className="text-stone-600 text-sm leading-relaxed flex-grow">
      "{review.text}"
    </p>
  </div>
);

export default function GoogleReviews() {
  const [reviews, setReviews] = useState([]);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Google Business Profile URLs
  const googleMapsUrl = 'https://maps.app.goo.gl/Wpfbt9LgTNeVEVCi8';
  // Direct link to leave a review - opens the Google Maps profile where users can leave a review
  const leaveReviewUrl = 'https://maps.app.goo.gl/Wpfbt9LgTNeVEVCi8';

  // Embla Carousel with Autoplay
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1,
    },
    [
      Autoplay({ 
        delay: 5000, 
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      })
    ]
  );

  // Track current slide
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  // Navigation handlers
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/google-reviews`);
        if (!response.ok) {
          throw new Error('Reviews not available');
        }
        const data = await response.json();
        setReviews(data.reviews || []);
        setBusinessInfo(data.business || null);
      } catch (err) {
        console.log('Google reviews not configured:', err.message);
        setError('Google reviews integration pending setup');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Loading state
  if (loading) {
    return (
      <section className="py-20 lg:py-28 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-8 w-64 bg-stone-200 rounded mb-4"></div>
              <div className="h-4 w-48 bg-stone-200 rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Fallback UI when no reviews available (business not verified in Places API)
  if (error || reviews.length === 0) {
    return (
      <section className="py-20 lg:py-28 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <GoogleLogo className="w-6 h-6" />
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-stone-900">
                What Our Verified Adopters Say
              </h2>
            </div>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Read authentic reviews from families who have adopted kittens from KittenState.
            </p>
          </div>

          <div className="max-w-lg mx-auto bg-white rounded-2xl p-8 shadow-lg border border-rose-100/50 text-center">
            <div className="flex justify-center mb-4">
              <GoogleLogo className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">
              See Our Google Reviews
            </h3>
            <p className="text-stone-600 mb-6">
              Visit our Google Business Profile to read verified reviews from real adopters.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-google-reviews-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#E85D75] text-white hover:bg-[#D64560] px-6 py-3 rounded-full font-medium transition-all hover:scale-105"
              >
                <GoogleLogo className="w-4 h-4" />
                View Reviews on Google
                <ExternalLink className="w-4 h-4" />
              </a>
              
              <a
                href={leaveReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leave-review-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#E85D75] border-2 border-[#E85D75] hover:bg-rose-50 px-6 py-3 rounded-full font-medium transition-all hover:scale-105"
              >
                <PenLine className="w-4 h-4" />
                Leave a Review
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Full carousel UI when reviews are available
  return (
    <section className="py-20 lg:py-28 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <GoogleLogo className="w-6 h-6" />
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-stone-900">
              What Our Verified Adopters Say
            </h2>
          </div>
          
          {/* Overall Rating */}
          {businessInfo && businessInfo.rating && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.round(businessInfo.rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-stone-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-lg font-semibold text-stone-800">
                {businessInfo.rating?.toFixed(1)}
              </span>
              <span className="text-stone-500">
                ({businessInfo.totalReviews} reviews)
              </span>
            </div>
          )}
          
          <p className="text-stone-600 max-w-2xl mx-auto">
            Real reviews from verified Google users who adopted kittens from KittenState.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative px-12 lg:px-16">
          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-stone-200 flex items-center justify-center hover:bg-rose-50 hover:border-[#E85D75] transition-all"
            aria-label="Previous review"
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 text-stone-600" />
          </button>
          
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-stone-200 flex items-center justify-center hover:bg-rose-50 hover:border-[#E85D75] transition-all"
            aria-label="Next review"
            data-testid="carousel-next-btn"
          >
            <ChevronRight className="w-5 h-5 text-stone-600" />
          </button>

          {/* Embla Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {reviews.map((review, index) => (
                <div
                  key={index}
                  className="flex-none w-full md:w-1/2 lg:w-1/3 pl-4 first:pl-0 md:first:pl-4"
                >
                  <div className="pr-4">
                    <ReviewCard review={review} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-[#E85D75] w-6'
                    : 'bg-stone-300 hover:bg-stone-400'
                }`}
                aria-label={`Go to review ${index + 1}`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-all-reviews-btn"
          >
            <Button
              className="bg-white text-stone-800 border border-stone-200 hover:border-[#E85D75] hover:bg-rose-50/50 rounded-full px-8 py-6 text-base font-medium transition-all inline-flex items-center gap-2"
            >
              <GoogleLogo className="w-4 h-4" />
              View All Reviews on Google Maps
              <ExternalLink className="w-4 h-4 ml-1" />
            </Button>
          </a>
          
          <a
            href={leaveReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leave-review-btn-full"
          >
            <Button
              className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full px-8 py-6 text-base font-medium transition-all inline-flex items-center gap-2 shadow-lg shadow-rose-200/50"
            >
              <PenLine className="w-4 h-4" />
              Leave a Review
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}
